// Chapter 8 - Reversal and Multielement Designs
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 8.
// All content traced to source-pdfs/Chapter-8-Reversal-and-Multielement-Designs-7.pdf

import type { ChapterModule } from "./types";

const chapter08Module: ChapterModule = {
  meta: {
    id: "08",
    number: 8,
    title: "Reversal and Multielement Designs",
    subtitle: "A-B-A-B, NCR, alternating treatments",
    part: "Part 3 - Evaluating and Analyzing Behavior Change",
    estMinutes: 75,
    bacb: ["D-4", "D-5"],
  },
  curated: true,
  overview: [
    "Chapter 8 unpacks two workhorse single-case designs. The REVERSAL DESIGN (A-B-A and A-B-A-B) demonstrates a functional relation by introducing and withdrawing the IV. The MULTIELEMENT (or alternating treatments) DESIGN compares two or more treatments by rapidly alternating them and watching for separation of data paths.",
    "You'll learn variations (B-A-B, multiple treatment reversal, NCR reversal, DRO/DRI/DRA reversals), the irreversibility and ethical concerns that constrain reversal designs, the three roles each data point plays in a multielement design, and the advantages and limits of multielement designs vs reversals.",
  ],
  objectives: [
    { id: "D-4", text: "Describe baseline logic and apply it to A-B-A, A-B-A-B, B-A-B, and multielement designs." },
    { id: "D-5", text: "Use reversal and alternating treatments designs to demonstrate experimental control." },
  ],
  coreConcepts: [
    {
      title: "Reversal tactic (A-B-A)",
      body: [
        "The reversal tactic entails repeated measures across three consecutive phases: (a) baseline (IV absent), (b) treatment (IV present), and (c) return to baseline. If behavior changes when the IV is present and returns to baseline levels when it is withdrawn, the design has provided initial evidence of a functional relation.",
      ],
    },
    {
      title: "A-B-A-B: the canonical reversal",
      body: [
        "Reintroducing the IV (the second B) is what makes the A-B-A-B design the most straightforward and generally most powerful intrasubject design for demonstrating a functional relation. It allows verification (return to baseline) AND replication (re-introducing IV reproduces the change). Repeated reversals further strengthen the demonstration.",
      ],
    },
    {
      title: "B-A-B: when initial baseline is not possible",
      body: [
        "The B-A-B variant starts with treatment, removes it (A), and reintroduces it (B). Use when an initial baseline phase is inappropriate or impossible for ethical or practical reasons (e.g., a dangerous behavior requires immediate intervention).",
      ],
    },
    {
      title: "Multiple treatment reversal designs",
      body: [
        "Designs like A-B-A-B-C-B-C or A-B-C-A-C-B-C use the reversal tactic to compare two or more treatments to baseline and to each other. They are particularly susceptible to SEQUENCE EFFECTS (one treatment carries over into the next condition).",
      ],
    },
    {
      title: "NCR and differential reinforcement reversals",
      body: [
        "Reversal control conditions can use noncontingent reinforcement (NCR), DRO (differential reinforcement of other behavior), or DRI/DRA (incompatible/alternative behavior). NCR isolates the contingent ASPECT of reinforcement (delivery is separated from the response). DRO/DRI/DRA reversals show whether the contingency itself, not just the reinforcer, is producing the effect.",
      ],
    },
    {
      title: "When NOT to use a reversal design",
      body: [
        "Reversals fail when: (a) the IV cannot be withdrawn once delivered (instruction, modeling, taught skills), (b) the behavior is irreversible once changed, or (c) withdrawing an effective treatment raises ethical/social concerns. Brief reversal phases - sometimes a single probe - may be enough to demonstrate control without prolonged exposure to baseline.",
      ],
    },
    {
      title: "Multielement / alternating treatments design",
      body: [
        "The multielement design compares two or more treatments while measuring their effects on the same DV. Conditions are rapidly alternated (e.g., session by session) and signaled by clear stimuli. Each data point for a specific treatment plays three roles: (a) prediction of future responding under that treatment, (b) verification of prior prediction, and (c) replication of previous effects produced by that treatment. Experimental control is demonstrated when the data paths for the two conditions show LITTLE OR NO OVERLAP.",
      ],
    },
    {
      title: "Variations of the multielement design",
      body: [
        "Common variations: single-phase alternating two or more conditions (one may be no-treatment); two-phase (baseline then comparison); three-phase (baseline, alternating, best-treatment-only); three-phase with participant choice; concurrent chains (schedule) design where the subject chooses between treatments.",
      ],
    },
    {
      title: "Advantages of multielement designs",
      body: [
        "Does not require treatment withdrawal; rapidly compares relative effectiveness; minimizes the irreversibility problem; minimizes sequence effects (because conditions alternate quickly); can be used with unstable data; can assess generalization; intervention can begin immediately.",
      ],
    },
    {
      title: "Limits of multielement designs",
      body: [
        "Susceptible to multiple treatment interference (effects of one condition carry over). Cap of ~4 conditions in an alternating-treatments phase. Most effective when treatments are clearly different from each other. Not effective for IVs that only work when implemented continuously over time. Rapid switching can feel artificial in clinical settings.",
      ],
    },
  ],
  glossary: [
    { term: "A-B design", definition: "A simple baseline-then-treatment design; provides only weak evidence because it lacks replication.", sourceHint: "A-B design (Key Terms list)." },
    { term: "A-B-A design", definition: "Baseline, treatment, return to baseline; demonstrates the IV's effect via withdrawal.", sourceHint: "The reversal tactic (A-B-A) entails repeated measures of behavior in a given setting during three consecutive phases." },
    { term: "A-B-A-B design", definition: "Baseline, treatment, baseline, treatment; the canonical reversal design.", sourceHint: "The A-B-A-B design is the most straightforward and generally most powerful intrasubject design for demonstrating functional relations." },
    { term: "B-A-B design", definition: "Treatment, baseline, treatment; used when initial baseline is inappropriate or impossible.", sourceHint: "The B-A-B reversal design can be used with target behaviors for which an initial baseline phase is inappropriate or not possible for ethical or practical reasons." },
    { term: "Reversal design", definition: "Family of designs (A-B-A, A-B-A-B, B-A-B, multiple-treatment reversal) using the reversal tactic.", sourceHint: "reversal design (Key Terms list)." },
    { term: "Reversal technique", definition: "Withdrawal or change of the IV used to evaluate its effect; includes NCR, DRO, DRI/DRA reversal variants.", sourceHint: "reversal technique (Key Terms list)." },
    { term: "Withdrawal design", definition: "Synonym for reversal design; emphasizes removal of the IV during the second A.", sourceHint: "withdrawal design (Key Terms list)." },
    { term: "Multiple treatment reversal design", definition: "Reversal design comparing two or more treatments (e.g., A-B-A-B-C-B-C).", sourceHint: "Multiple treatment reversal designs (e.g., A-B-A-B-C-B-C, A-B-C-A-C-B-C) use the reversal tactic to compare the effects of two or more experimental conditions." },
    { term: "Noncontingent reinforcement (NCR)", definition: "Reinforcer delivery on a fixed/variable time schedule independent of behavior; used as a control condition.", sourceHint: "The noncontingent reinforcement (NCR) reversal technique enables the isolation and analysis of the contingent aspect of reinforcement." },
    { term: "DRO reversal technique", definition: "Reversal phase using DRO (reinforcement contingent on the ABSENCE of the target behavior).", sourceHint: "Reversal techniques incorporating differential reinforcement control conditions (DRO and DRI/DRA) can also be used to analyze the effects of contingent reinforcement." },
    { term: "DRI/DRA reversal technique", definition: "Reversal phase using DRI (incompatible behavior) or DRA (alternative behavior).", sourceHint: "Reversal techniques incorporating differential reinforcement control conditions (DRO and DRI/DRA)." },
    { term: "Irreversibility", definition: "The property of a behavior that, once changed, will not return to baseline levels when the IV is withdrawn.", sourceHint: "Once changed during treatment, some behaviors will not reverse to baseline levels when the independent variable has been withdrawn. Such behavioral irreversibility precludes effective use of the reversal design." },
    { term: "Multielement design", definition: "A design that compares two or more treatments by rapidly alternating them while measuring their effects on the DV.", sourceHint: "The multielement design (also called alternating treatments design) compares two or more treatments while their effects on the target behavior are measured." },
    { term: "Alternating treatments design", definition: "Synonym for multielement design.", sourceHint: "(see multielement design definition)." },
    { term: "Adapted alternating treatments design", definition: "An alternating-treatments variant in which different treatments are applied to different but functionally similar target behaviors, controlling for behavior-specific learning.", sourceHint: "adapted alternating treatments design (Key Terms list)." },
    { term: "Sequence effects", definition: "Confounding influence of one experimental condition on subsequent conditions.", sourceHint: "Multiple treatment reversal designs are particularly susceptible to confounding by sequence effects." },
    { term: "Multiple treatment interference", definition: "Effects of one treatment carrying over and influencing performance during another treatment in the same design.", sourceHint: "Multielement designs are susceptible to multiple treatment interference." },
    { term: "Concurrent chains (schedule) design", definition: "A design in which the subject can choose between two or more treatments, revealing preference.", sourceHint: "Concurrent chains (or schedule) design (variations list)." },
  ],
  procedures: [
    {
      title: "Run an A-B-A-B design",
      intro: "The most powerful intrasubject design for demonstrating a functional relation.",
      steps: [
        { title: "Establish stable A1 baseline", detail: "Collect baseline data until variability is low; defer the IV." },
        { title: "Introduce B1 treatment", detail: "Deliver the IV; continue measurement; check for level/trend change." },
        { title: "Return to A2 baseline", detail: "Withdraw the IV; verify that behavior returns to baseline levels." },
        { title: "Re-introduce B2 treatment", detail: "Deliver the IV again; replicate the change observed in B1." },
        { title: "Decide on extra reversals", detail: "More reversals strengthen the demonstration; weigh against ethics and time." },
      ],
    },
    {
      title: "Choose between reversal and multielement design",
      intro: "Pick the design that matches your IV and behavior characteristics.",
      steps: [
        { title: "Is the IV reversible?", detail: "If no (e.g., instruction, modeling), avoid a withdrawal phase or use multielement." },
        { title: "Is the behavior reversible?", detail: "If acquired behaviors persist, multiple baseline (Ch 9) is better than reversal." },
        { title: "How urgent is intervention?", detail: "If immediate, B-A-B may be needed instead of A-B-A-B." },
        { title: "How many treatments to compare?", detail: "Multielement supports rapid comparison of 2-4 conditions." },
        { title: "Is sequence-effect risk high?", detail: "Multielement minimizes sequence effects via rapid alternation; multiple treatment reversal does not." },
      ],
    },
    {
      title: "Set up a multielement (alternating treatments) design",
      intro: "Each data point serves prediction, verification, and replication simultaneously.",
      steps: [
        { title: "Choose 2-4 distinct treatments", detail: "Treatments must be clearly different to produce visible separation." },
        { title: "Add a clear signaling stimulus", detail: "Different colored cards, sessions, or rooms cue which treatment is in effect." },
        { title: "Counterbalance the order", detail: "Randomly alternate or use a balanced sequence to minimize sequence effects." },
        { title: "Plot data paths together", detail: "Plot each treatment's data path on the same axes; visual separation = experimental control." },
        { title: "Optional best-treatment-only phase", detail: "After the alternating phase, run a single-treatment phase to confirm the winner's effect in isolation." },
      ],
    },
  ],
  examples: [
    { scenario: "Teacher introduces a token economy (B), tantrums drop, withdraws it (A2), tantrums return to baseline, reintroduces (B2), tantrums drop again.",
      application: "Classic A-B-A-B reversal demonstrating a functional relation between the IV (token economy) and DV (tantrums).",
      sourceHint: "The A-B-A-B design is the most straightforward and generally most powerful intrasubject design for demonstrating functional relations." },
    { scenario: "Researcher compares two prompting strategies by alternating them session-by-session for a learner; one strategy's data path stays consistently higher.",
      application: "Multielement / alternating treatments design; visible separation demonstrates experimental control without removing treatment.",
      sourceHint: "Experimental control is demonstrated in a multielement design when the data paths for two different treatments show little or no overlap." },
    { scenario: "Severe self-injury during baseline raises ethical concerns; the analyst starts with treatment, then briefly withdraws.",
      application: "B-A-B design - the initial baseline phase would be unethical.",
      sourceHint: "The B-A-B reversal design can be used with target behaviors for which an initial baseline phase is inappropriate or not possible for ethical or practical reasons." },
    { scenario: "An analyst pulls the contingent reinforcer but delivers the same reinforcer on a time schedule unrelated to behavior; rates plummet.",
      application: "NCR reversal technique - isolates the contingent ASPECT of reinforcement.",
      sourceHint: "The noncontingent reinforcement (NCR) reversal technique enables the isolation and analysis of the contingent aspect of reinforcement." },
  ],
  comparisons: [
    {
      title: "Reversal vs Multielement design",
      left:  { label: "Reversal (A-B-A-B)", points: [
        "Withdraws IV to verify functional relation.",
        "Powerful but requires reversibility.",
        "Ethical concerns when withdrawing a successful treatment.",
      ]},
      right: { label: "Multielement", points: [
        "Compares treatments via rapid alternation.",
        "No withdrawal phase needed.",
        "Minimizes sequence effects; cap of ~4 conditions.",
      ]},
    },
    {
      title: "A-B-A vs A-B-A-B",
      left:  { label: "A-B-A", points: [
        "One IV introduction + one withdrawal.",
        "Demonstrates effect but ends in baseline.",
        "Ethically uncomfortable (study ends without treatment).",
      ]},
      right: { label: "A-B-A-B", points: [
        "Two IV introductions + one withdrawal.",
        "Adds replication; study ends in treatment.",
        "Stronger evidence and better ethics.",
      ]},
    },
    {
      title: "Sequence effects vs Multiple treatment interference",
      left:  { label: "Sequence effects", points: [
        "Order of conditions influences later conditions.",
        "Big problem for multiple treatment REVERSAL designs.",
      ]},
      right: { label: "Multiple treatment interference", points: [
        "Co-presented treatments contaminate each other.",
        "Risk in multielement designs; mitigated by rapid alternation and isolated follow-up phase.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "A reversal design proves the IV is responsible for change.",
      reality: "Reversal designs demonstrate a functional relation, but interpretation still uses affirmation of the consequent. Other variables remain possible.",
      sourceHint: "Replication demonstrates the reliability of the behavior change and reduces the probability that some factor other than the independent variable was responsible (Chapter 7 §49).",
    },
    {
      myth: "Once a behavior change is observed, the analyst should never reverse it.",
      reality: "Reversal phases - sometimes brief probes - are necessary to demonstrate the functional relation. The decision balances scientific rigor with social/ethical concerns.",
      sourceHint: "Brief reversal phases, sometimes even one-session baseline probes, may be sufficient to demonstrate believable experimental control.",
    },
    {
      myth: "Multielement designs prevent treatment interference.",
      reality: "Multielement designs MINIMIZE but cannot eliminate multiple treatment interference. A follow-up isolated-treatment phase can confirm the winner.",
      sourceHint: "Multielement designs are susceptible to multiple treatment interference.",
    },
    {
      myth: "If the IV is instruction or modeling, a reversal design is fine.",
      reality: "Reversal designs fail with IVs that cannot be withdrawn once delivered, like teaching a skill.",
      sourceHint: "Experimental designs featuring the reversal tactic are ineffective in evaluating the effects of treatment variables that, by their very nature, cannot be withdrawn once presented (e.g., instruction, modeling).",
    },
    {
      myth: "More alternating treatments are always better.",
      reality: "Multielement designs cap effective comparison at ~4 conditions; more than that strains observers and dilutes the signal.",
      sourceHint: "An alternating treatments phase is usually limited to a maximum of four different treatment conditions.",
    },
  ],
  examChecklist: [
    "Describe the three phases of an A-B-A design and label IV and DV roles.",
    "Explain why A-B-A-B is preferred over A-B-A.",
    "Describe a situation that requires a B-A-B design.",
    "Distinguish reversal, withdrawal, and multielement designs.",
    "Define irreversibility and give one example.",
    "Describe NCR, DRO, and DRI/DRA reversal techniques.",
    "List the three roles each data point plays in a multielement design.",
    "List four advantages of the multielement design over reversal.",
    "Explain multiple treatment interference and how to mitigate it.",
    "Explain sequence effects in multiple-treatment reversal designs.",
  ],
  flashcardTerms: [
    "A-B design", "A-B-A design", "A-B-A-B design", "B-A-B design",
    "Reversal design", "Reversal technique", "Withdrawal design",
    "Multiple treatment reversal design",
    "Noncontingent reinforcement (NCR)", "DRO reversal technique", "DRI/DRA reversal technique",
    "Irreversibility",
    "Multielement design", "Alternating treatments design",
    "Adapted alternating treatments design",
    "Sequence effects", "Multiple treatment interference",
    "Concurrent chains (schedule) design",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which design is the MOST straightforward and generally MOST powerful intrasubject design for demonstrating a functional relation?",
      options: [
        { label: "A", text: "A-B" },
        { label: "B", text: "A-B-A" },
        { label: "C", text: "A-B-A-B" },
        { label: "D", text: "Multiple treatment reversal" },
      ],
      answer: "C",
      explanation: "A-B-A-B provides verification (withdrawal) AND replication (re-introduction) of the IV. It is the canonical reversal design.",
      topic: "Reversal designs",
      sourceHint: "The A-B-A-B design is the most straightforward and generally most powerful intrasubject design for demonstrating functional relations.",
    },
    {
      id: "Q2",
      question: "Why would a researcher use a B-A-B design instead of A-B-A-B?",
      options: [
        { label: "A", text: "To save time" },
        { label: "B", text: "Because initial baseline is inappropriate or impossible for ethical/practical reasons" },
        { label: "C", text: "Because the IV cannot be withdrawn" },
        { label: "D", text: "To eliminate practice effects" },
      ],
      answer: "B",
      explanation: "B-A-B is reserved for cases where running a baseline first would be unethical or impractical (e.g., severe self-injury).",
      topic: "Reversal variants",
      sourceHint: "The B-A-B reversal design can be used with target behaviors for which an initial baseline phase is inappropriate or not possible for ethical or practical reasons.",
    },
    {
      id: "Q3",
      question: "Experimental control in a multielement design is demonstrated by:",
      options: [
        { label: "A", text: "Significant difference on a t-test" },
        { label: "B", text: "Two-fold change in baseline rate" },
        { label: "C", text: "Little or no overlap between the data paths of the alternating treatments" },
        { label: "D", text: "Stable baseline before the design starts" },
      ],
      answer: "C",
      explanation: "Visible separation of the data paths is what demonstrates the IV's effect in a multielement design.",
      topic: "Multielement designs",
      sourceHint: "Experimental control is demonstrated in a multielement design when the data paths for two different treatments show little or no overlap.",
    },
    {
      id: "Q4",
      question: "Which reversal control condition isolates the CONTINGENT aspect of reinforcement?",
      options: [
        { label: "A", text: "DRO" },
        { label: "B", text: "DRI/DRA" },
        { label: "C", text: "Noncontingent reinforcement (NCR)" },
        { label: "D", text: "B-A-B" },
      ],
      answer: "C",
      explanation: "NCR delivers the reinforcer independent of behavior; if behavior changes when contingency is restored, the contingent aspect is responsible.",
      topic: "Reversal techniques",
      sourceHint: "The noncontingent reinforcement (NCR) reversal technique enables the isolation and analysis of the contingent aspect of reinforcement.",
    },
    {
      id: "Q5",
      question: "Which type of design is MOST susceptible to sequence effects?",
      options: [
        { label: "A", text: "A-B-A-B" },
        { label: "B", text: "Multiple treatment reversal (e.g., A-B-A-B-C-B-C)" },
        { label: "C", text: "Multielement (alternating treatments)" },
        { label: "D", text: "Concurrent chains" },
      ],
      answer: "B",
      explanation: "Multiple treatment reversal designs sequentially apply different treatments in long blocks, making sequence effects a major risk.",
      topic: "Sequence effects",
      sourceHint: "Multiple treatment reversal designs are particularly susceptible to confounding by sequence effects.",
    },
    {
      id: "Q6",
      question: "Which is NOT an advantage of the multielement (alternating treatments) design?",
      options: [
        { label: "A", text: "Does not require treatment withdrawal" },
        { label: "B", text: "Minimizes the irreversibility problem" },
        { label: "C", text: "Effective when only one treatment is implemented continuously" },
        { label: "D", text: "Can begin intervention immediately" },
      ],
      answer: "C",
      explanation: "Multielement designs are NOT effective for IVs that change behavior only when implemented continuously - they require alternation.",
      topic: "Multielement design",
      sourceHint: "Multielement designs are not effective for assessing the effects of independent variables that change behavior only when implemented over a continuous period of time.",
    },
    {
      id: "Q7",
      question: "Each data point in a multielement design plays THREE roles for its specific treatment. Which is NOT one of them?",
      options: [
        { label: "A", text: "Prediction" },
        { label: "B", text: "Verification" },
        { label: "C", text: "Replication" },
        { label: "D", text: "Random control" },
      ],
      answer: "D",
      explanation: "The three roles are prediction, verification, and replication - all three are achieved by each data point in the alternating treatments phase.",
      topic: "Multielement logic",
      sourceHint: "In a multielement design, each successive data point for a specific treatment plays three roles: (a) a basis for the prediction of future levels of responding under that treatment, (b) potential verification of the previous prediction, and (c) the opportunity for replication of previous effects.",
    },
  ],
  reviewPrompts: [
    "Draw an A-B-A-B data set with a clear functional relation; label A1, B1, A2, B2 and the prediction/verification/replication elements.",
    "Identify a case where reversal is ethically problematic and propose a multielement alternative.",
    "Describe how NCR, DRO, and DRI/DRA reversal techniques would each test the contingent aspect of reinforcement.",
    "Sketch a multielement design comparing three prompting strategies, with the signaling stimulus and balanced order.",
    "Explain when adding a best-treatment-only phase after alternating treatments adds inference.",
    "Defend or critique the use of an A-B-A design for evaluating a teacher-led skill instruction program.",
  ],
  recap: [
    "Reversal designs (A-B, A-B-A, A-B-A-B, B-A-B, multiple treatment reversal) demonstrate functional relations by introducing and withdrawing the IV. A-B-A-B is the gold standard intrasubject design when reversibility and ethics allow.",
    "Multielement (alternating treatments) designs rapidly alternate two or more treatments and rely on data-path separation for experimental control. Each point serves prediction, verification, and replication simultaneously.",
    "Use multielement when the IV cannot be withdrawn (instruction, modeling), when behavior is irreversible, when intervention must start immediately, or when comparing several treatments. Cap conditions at ~4 and follow with an isolated-treatment phase when interference is a concern.",
  ],
  sourceExcerpts: [
    {
      heading: "Reversal tactic",
      text: "The reversal tactic (A-B-A) entails repeated measures of behavior in a given setting during three consecutive phases: (a) baseline (independent variable absent), (b) treatment (independent variable present), and (c) return to baseline (independent variable absent). Reintroducing the independent variable in the form of an A-B-A-B design strengthens the reversal design tremendously.",
    },
    {
      heading: "When reversal fails",
      text: "Experimental designs featuring the reversal tactic are ineffective in evaluating the effects of treatment variables that, by their very nature, cannot be withdrawn once presented (e.g., instruction, modeling). Once changed during treatment, some behaviors will not reverse to baseline levels when the independent variable has been withdrawn. Such behavioral irreversibility precludes effective use of the reversal design.",
    },
    {
      heading: "Multielement design control",
      text: "In a multielement design, each successive data point for a specific treatment plays three roles: (a) a basis for the prediction of future levels of responding under that treatment, (b) potential verification of the previous prediction of performance under that treatment, and (c) the opportunity for replication of previous effects produced by that treatment. Experimental control is demonstrated in a multielement design when the data paths for two different treatments show little or no overlap.",
    },
    {
      heading: "Advantages of multielement",
      text: "Advantages of the multielement (alternating treatments) design include the following: does not require treatment withdrawal; rapidly compares the relative effectiveness of treatments; minimizes the irreversibility problem; minimizes sequence effects; can be used with unstable data patterns; can assess generalization of effects; intervention can begin immediately.",
    },
  ],
};

export default chapter08Module;
